import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { dichiarazioneAgenzia, euro } from '../../utils/scheda';
import { altriPortali } from '../../utils/immobili';
import { giornoEMese, leggiDataUtc, migliaia } from '../../utils/percorso';
import Icona from '../cliente/Icone';
import { errore } from './comuni';

// La ricerca della casa nella scheda cliente dello staff (backend/corrispondenza.py, ricerca_automatica.py, archivio.py):
// - "Nuovi annunci per {nome}": dalla ricerca automatica, con il punteggio da 0 a 100, il perché e cosa manca;
// - "Case in archivio per {nome}": case di altri clienti con punteggio da 60 (il cliente non sa da dove vengono), e
//   prima di proporne una si verifica che ci sia ancora (ricontrolli.py: "Proponi" spento con «verifica in corso»);
// - "Stessa casa per due clienti": l'avviso quando un'altra pratica ha la stessa casa attiva;
// - "Ricerche automatiche": gli indirizzi per portale (si correggono), i filtri nell'indirizzo e quelli applicati
//   dopo, l'ultima volta, trovati / nuovi / proposti / ignorati, acceso o spento per il cliente, "Cerca ora".
// Su ogni annuncio nuovo e su ogni casa in archivio c'è «È un'asta» (backend/aste_apprendimento.py): quello che
// sfugge al filtro lo segna lo staff, e da lì il sistema impara le agenzie d'aste e le parole nuove.

export const PORTALI = { immobiliare: 'immobiliare.it', idealista: 'idealista', casa: 'casa.it' };

// La foto di un annuncio trovato. Il server manda la miniatura su R2 (480 px, ~20 KB; backend/miniature.py), che
// è dello stesso dominio dell'app: nessun CDN di portale da cui farsi rifiutare. Quando non c'è — annunci vecchi
// senza miniatura, foto cancellata, rete che cade — si mostra un riquadro scuro con scritto cosa manca e da che
// portale veniva. Mai l'icona dell'immagine rotta: diceva «è tutto guasto» quando mancava solo una fotografia.
export const FotoAnnuncio = ({ a }) => {
  const [rotta, setRotta] = useState(false);
  if (!a.foto || rotta) {
    return (
      <div className="im vuota" data-testid={`foto-vuota-${a.id}`}>
        <span>foto non disponibile{PORTALI[a.portale] ? <i>{PORTALI[a.portale]}</i> : null}</span>
      </div>
    );
  }
  return <img className="im" src={a.foto} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setRotta(true)} />;
};
const unisci = (parti) => parti.filter(Boolean).reduce((acc, x, n) => (n ? [...acc, ' · ', x] : [x]), []);

// Il punteggio da 0 a 100: verde da 60 (va anche agli altri clienti), giallo da 40 (si propone), grigio sotto
export const Punteggio = ({ valore }) => (
  <span className={`punti ${valore >= 60 ? 'ok' : valore >= 40 ? 'w' : 'no'}`} title="Punteggio da 0 a 100 (pesi in Impostazioni → Estrattore)">{valore}</span>
);

// "perché nel budget, 3 locali, Vimercate, giardino" · "manca 2° bagno" · "da verificare box"
export const PercheManca = ({ punteggio, perche, manca, verificare }) => {
  const testo = (x) => (Array.isArray(x) ? x.join(', ') : x);
  return (
    <div className="righe-perche" data-testid="perche">
      {(punteggio !== undefined && punteggio !== null) || testo(perche) ? (
        <div>{punteggio !== undefined && punteggio !== null && <Punteggio valore={punteggio} />} {testo(perche) && <><b>perché</b>{testo(perche)}</>}</div>
      ) : null}
      {testo(manca) && <div className="manca"><b>manca</b>{testo(manca)}</div>}
      {testo(verificare) && <div className="verificare"><b>da verificare</b>{testo(verificare)}</div>}
    </div>
  );
};

// L'agenzia di un annuncio trovato: "Casa Brianza · Tecnocasa · Vimercate · Dichiarata ✓ · ref. Matteo (titolare) 333 …"
// L'etichetta è la DICHIARAZIONE (Dichiarata ✓ / In attesa / Non dichiara, utils/scheda.js): è quella che conta per
// decidere cosa proporre e per il punteggio. La collaborazione si legge solo nella scheda dell'agenzia.
export const AgenziaAnnuncio = ({ agenzia, nome }) => {
  if (!agenzia) return nome ? <span className="ag-annuncio">{nome}</span> : <span className="ag-annuncio muto">agenzia non indicata</span>;
  const ref = agenzia.referente;
  const d = dichiarazioneAgenzia(agenzia.stato);
  return (
    <span className="ag-annuncio" data-testid="agenzia-annuncio">
      <i style={{ background: d.colore }} />
      {agenzia.id ? <Link to={`/admin/agenzie/${agenzia.id}`}>{agenzia.etichetta}</Link> : agenzia.etichetta}
      {/* agenzia che vende aste di mestiere: i suoi annunci non si propongono a nessuno (campo `aste` sulla scheda) */}
      {agenzia.aste && <span className="tag t-r" style={{ marginLeft: '.3rem' }} data-testid="agenzia-aste">Aste</span>}
      {' · '}<b className={`st-${agenzia.stato}`} style={{ color: d.colore }}>{d.testo}</b>
      {ref && <>{' · ref. '}{ref.nome}{ref.ruolo ? ` (${ref.ruolo})` : ''}{ref.telefono ? ` ${ref.telefono}` : ''}</>}
    </span>
  );
};

// ---------------------------------------------------------------- «È un'asta» (backend/aste_apprendimento.py)

// Lo staff segna l'asta sfuggita al filtro: la casa esce dalle proposte e dai suggerimenti, l'agenzia viene
// contata e alla seconda asta esce anche lei; le parole di quegli annunci si propongono in Impostazioni → Aste.
export const segnalaAsta = async ({ immobileId, propostaId }) => {
  const r = await axios.post(`${API}/aste/segnala`, { immobile_id: immobileId, proposta_id: propostaId });
  const ag = r.data?.agenzia;
  toast.success(ag?.diventata
    ? `Segnata come asta. ${ag.etichetta}: seconda asta, non la proponiamo più`
    : 'Segnata come asta: esce dalle proposte e dai suggerimenti');
  return r.data;
};

export const BottoneAsta = ({ immobileId, propostaId, attesa, onFatto, taglia = 'sm' }) => (
  <button type="button" className={`btn ${taglia} rosso-vuoto`} disabled={attesa}
    title="Asta giudiziaria: la casa esce dalle proposte e l'agenzia viene contata"
    onClick={async () => {
      try { await segnalaAsta({ immobileId, propostaId }); onFatto(); } catch (e) { errore(e); }
    }}>È un&apos;asta</button>
);

// L'altro lato della stessa decisione. Per un annuncio nuovo fermo su «sospetta asta» (prezzo troppo basso per la
// zona senza un motivo scritto, backend/ricerca_automatica.py) lo staff dice che si può proporre; per una casa
// segnata per sbaglio toglie la segnalazione (backend/aste_apprendimento.py).
export const segnalaNonAsta = async ({ immobileId, propostaId }) => {
  if (propostaId) {
    await axios.post(`${API}/proposte-automatiche/${propostaId}/non-e-asta`);
    toast.success('Segnato come non asta: adesso si può proporre');
    return;
  }
  await axios.post(`${API}/aste/non-e-asta`, { immobile_id: immobileId });
  toast.success("Non è un'asta: la casa torna fra quelle che si possono proporre");
};

export const BottoneNonAsta = ({ immobileId, propostaId, attesa, onFatto, taglia = 'sm' }) => (
  <button type="button" className={`btn ${taglia} p`} disabled={attesa}
    title="L'abbiamo guardato: non è un'asta, si può proporre"
    onClick={async () => {
      try { await segnalaNonAsta({ immobileId, propostaId }); onFatto(); } catch (e) { errore(e); }
    }}>Non è un&apos;asta</button>
);

// «sospetta asta»: l'annuncio c'è ma non si propone finché qualcuno non l'ha guardato
export const SOSPETTA = 'sospetta';
export const TagSospetta = ({ a }) => (a.stato !== SOSPETTA ? null : (
  <div className="m" data-testid={`sospetta-${a.id}`}>
    <span className="tag t-w" style={{ marginRight: '.3rem' }}>sospetta asta</span>
    {a.sospetta_perche || 'prezzo troppo basso per la zona'}
  </div>
));

// «Anche su idealista.it · casa.it»: la stessa casa pubblicata su più portali è una riga sola (backend/doppioni.py)
export const AncheSu = ({ annuncio }) => {
  const altri = altriPortali(annuncio);
  if (!altri.length) return null;
  return (
    <div className="m" data-testid={`anche-su-${annuncio.id}`}>
      Anche su{' '}
      {altri.map((v, n) => (
        <React.Fragment key={v.link}>
          {n > 0 && ' · '}
          <a href={v.link} target="_blank" rel="noopener noreferrer">{v.nome}</a>
        </React.Fragment>
      ))}
    </div>
  );
};

// Un annuncio trovato in breve (Oggi → Dai clienti): foto, titolo, prezzo, punteggio con perché e manca, agenzia
export const AnnuncioBreve = ({ a, onAsta }) => (
  <div className="annuncio-breve" data-testid={`annuncio-breve-${a.id}`}>
    <FotoAnnuncio a={a} />
    <div style={{ minWidth: 0 }}>
      <a className="t" href={a.link} target="_blank" rel="noopener noreferrer">{a.titolo || a.link}</a>
      <div className="m">{unisci([euro(a.prezzo) && <b key="p">{euro(a.prezzo)}</b>, a.superficie && `${a.superficie} m²`, PORTALI[a.portale]])}</div>
      <PercheManca punteggio={a.punteggio} perche={a.motivi} manca={a.manca} verificare={a.verificare} />
      <TagSospetta a={a} />
      <AncheSu annuncio={a} />
      <div className="m"><AgenziaAnnuncio agenzia={a.agenzia} nome={a.agenzia_nome} /></div>
      {onAsta && (
        <div className="m" style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          <BottoneAsta propostaId={a.id} onFatto={onAsta} taglia="xs" />
          {a.stato === SOSPETTA && <BottoneNonAsta propostaId={a.id} onFatto={onAsta} taglia="xs" />}
        </div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------- nuovi annunci della ricerca automatica

export const AnnunciNuovi = ({ cliente, versione, onFatto, isAdmin }) => {
  const [annunci, setAnnunci] = useState(null);
  const [tutti, setTutti] = useState(false);
  const [attesa, setAttesa] = useState(null);
  const carica = useCallback(() => axios.get(`${API}/clienti/${cliente.id}/annunci-nuovi`).then((r) => setAnnunci(r.data?.annunci || [])).catch(() => setAnnunci([])), [cliente.id]);
  useEffect(() => { carica(); }, [carica, versione]);
  useEffect(() => {
    if (annunci?.length && window.location.hash === '#annunci') document.getElementById('annunci')?.scrollIntoView({ block: 'start' });
  }, [annunci]);
  if (!Array.isArray(annunci) || !annunci.length) return null;
  const azione = async (a, cosa, testo) => {
    setAttesa(a.id);
    try {
      await axios.post(`${API}/proposte-automatiche/${a.id}/${cosa}`);
      toast.success(testo);
      carica();
      if (cosa === 'proponi') onFatto();
    } catch (e) { errore(e); } finally { setAttesa(null); }
  };
  const elenco = tutti ? annunci : annunci.slice(0, 5);
  return (
    <section className="card r" id="annunci" data-testid="annunci-nuovi">
      <div className="ttl"><div className="i"><Icona nome="cerca" /></div>
        {/* «aste escluse»: un'asta giudiziaria non si propone e non si suggerisce mai (backend/aste.py) */}
        <h2>Nuovi annunci per {cliente.nome}: {annunci.length} <small>dalla ricerca automatica · aste escluse</small></h2></div>
      {elenco.map((a) => (
        <div className="prop" key={a.id} data-testid={`annuncio-${a.id}`}>
          <FotoAnnuncio a={a} />
          <div style={{ minWidth: 0 }}>
            <h4><a href={a.link} target="_blank" rel="noopener noreferrer">{a.titolo || a.link}</a></h4>
            <div className="m">{unisci([euro(a.prezzo) && <b key="p">{euro(a.prezzo)}</b>, a.superficie && `${a.superficie} m²`, a.locali && `${a.locali} loc`,
              PORTALI[a.portale], a.zona || a.comune])}</div>
            <PercheManca punteggio={a.punteggio} perche={a.motivi} manca={a.manca} verificare={a.verificare} />
            <TagSospetta a={a} />
            <AncheSu annuncio={a} />
            <div className="m"><AgenziaAnnuncio agenzia={a.agenzia} nome={a.agenzia_nome} /></div>
          </div>
          <div className="acts"><div className="bt">
            {/* una «sospetta asta» non si propone finché qualcuno non l'ha guardata: il server risponderebbe 409 */}
            <button type="button" className="btn sm p" disabled={attesa === a.id || a.stato === SOSPETTA}
              title={a.stato === SOSPETTA ? "Prima bisogna dire se è un'asta o no" : undefined}
              onClick={() => azione(a, 'proponi', `Proposto a ${cliente.nome}: le arriva «Volete vederla?»`)}>Proponi</button>
            <button type="button" className="btn sm" disabled={attesa === a.id} onClick={() => azione(a, 'ignora', 'Ignorato: non torna più')}>Ignora</button>
            {/* i due lati della stessa decisione: asta sfuggita al filtro, oppure sospetta che asta non è */}
            <BottoneAsta propostaId={a.id} attesa={attesa === a.id} onFatto={carica} />
            {a.stato === SOSPETTA && <BottoneNonAsta propostaId={a.id} attesa={attesa === a.id} onFatto={carica} />}
            {/* non è l'etichetta di uno stato ma un'azione: l'agenzia esce e i suoi annunci spariscono per tutti
                (backend/ricerca_automatica.py, non-collaborano). La collaborazione si legge solo sulla sua scheda. */}
            {isAdmin && a.agenzia_nome && <button type="button" className="btn sm" disabled={attesa === a.id}
              title="I suoi annunci spariscono per tutti i clienti"
              onClick={() => azione(a, 'non-collaborano', `${a.agenzia_nome}: non lavoriamo con loro, i suoi annunci spariscono`)}>Non lavoriamo con loro</button>}
          </div></div>
        </div>
      ))}
      {annunci.length > 5 && <button type="button" className="btn sm" style={{ marginTop: '.6rem' }} onClick={() => setTutti(!tutti)}>{tutti ? 'Meno' : `Tutti (${annunci.length})`}</button>}
    </section>
  );
};

// ---------------------------------------------------------------- case in archivio

// Non si propone una casa che forse non c'è più (backend/ricontrolli.py): se l'ultimo controllo è più vecchio di 7
// giorni la casa va in coda all'estensione con priorità alta e "Proponi" resta spento con «verifica in corso»,
// finché non si sa l'esito. Senza il link dell'annuncio l'estensione non ha niente da aprire: la disponibilità la
// conferma a mano lo staff ("Ancora disponibile"), e lo stesso vale se la verifica non riesce (captcha, rete).
export const ATTESA_VERIFICA = 20000;  // ogni 20 secondi si richiede l'esito delle verifiche ancora in corso
const CONFERMA_A_MANO = ['serve_conferma', 'verifica_fallita'];
// finché non si sa com'è andata (compreso il momento prima della risposta) la casa non si propone
const IN_CORSO = { stato: 'in_corso', proponibile: false, testo: 'verifica in corso' };

export const CaseInArchivio = ({ cliente, versione, onFatto }) => {
  const [dati, setDati] = useState(null);
  const [verifiche, setVerifiche] = useState(null);
  const [attesa, setAttesa] = useState(null);
  const [tutte, setTutte] = useState(false);
  useEffect(() => {
    axios.get(`${API}/clienti/${cliente.id}/archivio`).then((r) => setDati(r.data)).catch(() => setDati(null));
  }, [cliente.id, versione]);
  useEffect(() => {
    if (dati?.case?.length && window.location.hash === '#archivio') document.getElementById('archivio')?.scrollIntoView({ block: 'start' });
  }, [dati]);
  // gli id in una stringa: così il giro delle verifiche riparte quando cambiano le case, non a ogni ridisegno
  const idCase = (dati?.case || []).map((c) => c.id).join(',');
  useEffect(() => {
    if (!idCase) return undefined;
    let vivo = true;
    let attesaGiro = null;
    const giro = async () => {
      let stati = {};
      try {
        const r = await axios.post(`${API}/immobili/verifica-prima-di-proporre`, { immobili: idCase.split(','), cliente_id: cliente.id });
        stati = r.data?.stati || {};
      } catch { stati = {}; }
      if (!vivo) return;
      setVerifiche(stati);
      if (Object.values(stati).some((v) => v?.stato === 'in_corso')) attesaGiro = setTimeout(giro, ATTESA_VERIFICA);
    };
    giro();
    return () => { vivo = false; if (attesaGiro) clearTimeout(attesaGiro); };
  }, [idCase, cliente.id]);
  if (!dati?.motivo || !Array.isArray(dati.case) || !dati.case.length) return null;
  const proponi = async (casa) => {
    setAttesa(casa.id);
    try {
      await axios.post(`${API}/clienti/${cliente.id}/archivio/${casa.id}/proponi`);
      toast.success(`Proposta a ${cliente.nome}: le arriva «Volete vederla?»`);
      onFatto();
    } catch (e) { errore(e); } finally { setAttesa(null); }
  };
  const confermaAMano = async (casa) => {
    setAttesa(casa.id);
    try {
      const r = await axios.post(`${API}/immobili/${casa.id}/ancora-disponibile`);
      setVerifiche((v) => ({ ...(v || {}), [casa.id]: { ...(v?.[casa.id] || {}), ...r.data } }));
      toast.success('Segnata come ancora disponibile: adesso si può proporre');
    } catch (e) { errore(e); } finally { setAttesa(null); }
  };
  const elenco = tutte ? dati.case : dati.case.slice(0, 4);
  return (
    <section className="card r" id="archivio" data-testid="case-in-archivio">
      <div className="ttl"><div className="i"><Icona nome="stella" /></div>
        {/* chi è in fase Visita cerca ancora casa: l'etichetta lo dice, invece di scrivere "in ricerca" a tutti */}
        <h2>Case in archivio per {cliente.nome} <small>{dati.totale} · {dati.motivo === 'fermo' ? 'fermo da un po\'' : dati.motivo === 'visita' ? 'in visita' : 'in ricerca'} · aste escluse</small></h2></div>
      {elenco.map((c) => {
        const v = verifiche?.[c.id] || IN_CORSO;
        // il prezzo che conta è quello dell'ultima verifica: se è cambiato si propone con il nuovo
        const prezzo = v.prezzo || v.prezzo === 0 ? v.prezzo : c.prezzo;
        // l'agenzia della casa si legge dalla dichiarazione, come negli annunci nuovi
        const dich = c.agenzia ? dichiarazioneAgenzia(c.agenzia_stato) : null;
        return (
        <div className="prop" key={c.id} data-testid={`archivio-${c.id}`}>
          <FotoAnnuncio a={c} />
          <div style={{ minWidth: 0 }}>
            <h4>{c.titolo || c.indirizzo}</h4>
            <div className="m">{unisci([euro(prezzo) && <b key="p">{euro(prezzo)}</b>, c.superficie, c.locali && `${c.locali} loc`,
              dich && <span key="ag" className="ag" data-testid={`agenzia-archivio-${c.id}`}><i style={{ background: dich.colore }} />{c.agenzia}
                {' · '}<b style={{ color: dich.colore }}>{dich.testo}</b></span>, c.stato])}</div>
            <PercheManca punteggio={c.punteggio} perche={c.perche} manca={c.manca} verificare={c.verificare} />
            <div className={`verifica-casa${v.proponibile ? ' ok' : ''}`} data-testid={`verifica-${c.id}`}>{v.testo}</div>
          </div>
          <div className="acts"><div className="bt">
            <button type="button" className="btn sm p" disabled={attesa === c.id || !v.proponibile}
              title={v.proponibile ? undefined : v.testo} onClick={() => proponi(c)}>{attesa === c.id ? 'Propongo…' : 'Proponi'}</button>
            {CONFERMA_A_MANO.includes(v.stato) && (
              <button type="button" className="btn sm" disabled={attesa === c.id} onClick={() => confermaAMano(c)}>Ancora disponibile</button>
            )}
            {/* asta sfuggita al filtro: la casa esce da qui e da tutti i suggerimenti, l'agenzia viene contata */}
            <BottoneAsta immobileId={c.id} attesa={attesa === c.id} onFatto={onFatto} />
            <Link className="btn sm" to={`/immobili/${c.id}`}>Apri</Link>
          </div></div>
        </div>
        );
      })}
      {dati.case.length > 4 && <button type="button" className="btn sm" style={{ marginTop: '.6rem' }} onClick={() => setTutte(!tutte)}>{tutte ? 'Meno' : `Tutte (${dati.case.length})`}</button>}
    </section>
  );
};

// ---------------------------------------------------------------- stessa casa per due clienti

const STATI_BREVI = { nuovo: 'nuovo', da_contattare: 'da contattare', non_risponde: 'non risponde', richiamare: 'da richiamare',
  da_visionare: 'da vedere', visita_fissata: 'visita fissata', proposta_in_corso: 'proposta in corso', rogito_in_corso: 'al rogito' };

export const CaseCondivise = ({ cliente, versione }) => {
  const [case_, setCase] = useState([]);
  useEffect(() => {
    axios.get(`${API}/clienti/${cliente.id}/case-condivise`).then((r) => setCase(Array.isArray(r.data?.case) ? r.data.case : [])).catch(() => setCase([]));
  }, [cliente.id, versione]);
  if (!case_.length) return null;
  return (
    <div className="condivise" role="note" data-testid="case-condivise">
      <Icona nome="avviso" />
      <div>
        <b>Stessa casa attiva per {case_.length === 1 ? 'un altro cliente' : 'altri clienti'}</b>
        {case_.map((c) => (
          <span key={c.immobile_id}>
            {c.indirizzo} è anche tra le case di {c.altri.map((a, n) => (
              <React.Fragment key={`${a.cliente_id || 'x'}-${n}`}>{n ? (n === c.altri.length - 1 ? ' e ' : ', ') : ''}
                {a.cliente_id ? <Link to={`/admin/clienti/${a.cliente_id}${a.immobile_id ? `?evidenzia=${a.immobile_id}` : ''}#immobili`}>{a.nome}</Link> : a.nome}
                {STATI_BREVI[a.stato] ? ` (${STATI_BREVI[a.stato]})` : ''}</React.Fragment>
            ))}.
          </span>
        ))}
        <small>Mettetevi d'accordo prima di fissare visite o proposte: al cliente non si dice niente.</small>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- ricerche automatiche

const quanti = (n, una, tante) => `${n} ${n === 1 ? una : tante}`;
const nomiFiltri = (ids, filtri) => (ids || []).map((k) => (filtri.find((f) => f.id === k) || { nome: k }).nome).join(', ');

const RigaRicerca = ({ r, filtri, onSalva }) => {
  const [modifica, setModifica] = useState(null);
  const esito = r.ultimo_esito;
  const ultima = leggiDataUtc(r.ultima_esecuzione);
  const numeri = esito ? [`${quanti(esito.letti, 'trovato', 'trovati')}${esito.totale ? ` su ${migliaia(esito.totale)}` : ''}`, quanti(esito.nuovi, 'nuovo', 'nuovi')] : [];
  const salva = async (corpo) => { if (await onSalva(r, corpo)) setModifica(null); };
  return (
    <div className={`prop ricerca-auto${r.attiva ? '' : ' spento'}`} data-testid={`ricerca-${r.portale}-${r.comune_norm}`}>
      <div style={{ minWidth: 0 }}>
        <h4>{PORTALI[r.portale]} {r.in_coda && <span className="tag t-b">in coda</span>} {!r.attiva && <span className="tag t-r">spenta</span>} {r.manuale && <span className="tag t-o">scritto da voi</span>}</h4>
        {modifica !== null ? (
          <div className="form-riga" style={{ marginTop: '.3rem' }}>
            <input value={modifica} onChange={(e) => setModifica(e.target.value)} aria-label={`Indirizzo della ricerca su ${PORTALI[r.portale]}`} style={{ flex: 1, minWidth: '12rem' }} />
            <button type="button" className="btn xs p" onClick={() => salva({ url: modifica })}>Salva</button>
            {r.manuale && <button type="button" className="btn xs" onClick={() => salva({ url: '' })}>Torna a quello calcolato</button>}
            <button type="button" className="btn xs" onClick={() => setModifica(null)}>Annulla</button>
          </div>
        ) : <div className="m url">{r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer">{r.url}</a> : r.nota}</div>}
        {(r.filtri_link?.length > 0 || r.filtri_dopo?.length > 0) && (
          <div className="m filtri-ricerca">
            {r.filtri_link?.length > 0 && <span><b>nel link</b> {nomiFiltri(r.filtri_link, filtri)}</span>}
            {r.filtri_dopo?.length > 0 && <span><b>dopo</b> {nomiFiltri(r.filtri_dopo, filtri)}</span>}
          </div>
        )}
        <div className="m">{ultima ? `ultima: ${giornoEMese(ultima)}` : 'mai fatta'}{numeri.length ? ` · ${numeri.join(' · ')}` : ''}
          {` · ${quanti(r.conti?.proposti || 0, 'proposto', 'proposti')} · ${quanti(r.conti?.ignorati || 0, 'ignorato', 'ignorati')}`}{r.conti?.da_guardare ? ` · ${r.conti.da_guardare} da guardare` : ''}
          {esito?.sotto_soglia ? ` · ${esito.sotto_soglia} sotto soglia` : ''}{r.ultimo_errore ? ` · non riuscita (${r.ultimo_errore})` : ''}</div>
      </div>
      <div className="acts"><div className="bt">
        <button type="button" className="btn sm" onClick={() => setModifica(r.url || '')}>Correggi</button>
        {r.url && <button type="button" className={`btn sm${r.attiva ? '' : ' p'}`} onClick={() => onSalva(r, { attiva: !r.attiva })}>{r.attiva ? 'Spegni' : 'Accendi'}</button>}
      </div></div>
    </div>
  );
};

export const RicercheAutomatiche = ({ cliente, versione }) => {
  const [dati, setDati] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const carica = useCallback(() => axios.get(`${API}/clienti/${cliente.id}/ricerche`).then((r) => setDati(r.data)).catch(() => setDati(null)), [cliente.id]);
  useEffect(() => { carica(); }, [carica, versione]);
  const salva = async (r, corpo) => {
    try { await axios.put(`${API}/ricerche/${r.id}`, corpo); carica(); return true; } catch (e) { errore(e); return false; }
  };
  const interruttore = async (attiva) => {
    try { const r = await axios.put(`${API}/clienti/${cliente.id}/ricerca-automatica`, { attiva }); setDati(r.data); toast.success(attiva ? `Accesa per ${cliente.nome}` : `Spenta per ${cliente.nome}`); }
    catch (e) { errore(e); }
  };
  const cercaOra = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/clienti/${cliente.id}/ricerche/cerca-ora`);
      setDati(r.data);
      toast.success(r.data.messe ? `${r.data.messe === 1 ? '1 ricerca' : `${r.data.messe} ricerche`} in coda: l'estensione le fa per prime` : 'Già in coda: l\'estensione le fa per prime');
    } catch (e) { errore(e); } finally { setAttesa(false); }
  };
  if (!Array.isArray(dati?.ricerche)) return null;
  const filtri = dati.filtri || [];
  const comuni = [...new Set(dati.ricerche.map((r) => r.comune))];
  const accese = dati.ricerche.filter((r) => r.attiva).length;
  return (
    <section className="card r" id="ricerche" data-testid="ricerche-automatiche">
      <div className="ttl"><div className="i b"><Icona nome="cerca" /></div><h2>Ricerche automatiche <small>{accese} accese</small></h2>
        <label className="spunta"><input type="checkbox" checked={dati.attiva} onChange={(e) => interruttore(e.target.checked)} aria-label={`Ricerca automatica per ${cliente.nome}`} />
          {dati.attiva ? 'accesa' : 'spenta'} per {cliente.nome}</label>
        <button type="button" className="btn sm p" disabled={attesa || !dati.attiva || !accese} onClick={cercaOra}>{attesa ? 'Metto in coda…' : 'Cerca ora'}</button></div>
      {filtri.length > 0 && (
        <div className="scelte-ricerca" aria-label="Criteri della ricerca"><span>Criteri</span>{filtri.map((f) => <span key={f.id} className="chip">{f.nome}</span>)}</div>
      )}
      {!dati.ricerche.length && <p className="vuoto">{dati.manca === 'budget' ? 'Nessuna ricerca: manca il budget massimo.' : dati.manca === 'comuni'
        ? 'Nessuna ricerca: servono i comuni (fino a 30) nella ricerca del cliente.' : !dati.in_ricerca ? `Nessuna ricerca: ${cliente.nome} non è più in fase Ricerca o Visita.` : 'Nessuna ricerca.'}</p>}
      {comuni.map((comune) => (
        <div key={comune} className="ricerche-comune">
          <p className="kk">{comune}</p>
          {dati.ricerche.filter((r) => r.comune === comune).map((r) => <RigaRicerca key={r.id} r={r} filtri={filtri} onSalva={salva} />)}
        </div>
      ))}
      <p className="muto" style={{ fontSize: '.76rem', marginTop: '.6rem' }}>
        Ogni giorno in orario d'ufficio, dopo gli annunci da leggere; al massimo {dati.limite} ricerche al giorno per tutti i clienti. «Nel link» li
        filtra il portale, «dopo» li controlliamo noi sulle schede lette: quello che manca abbassa il punteggio. Sotto soglia non si propone.
      </p>
    </section>
  );
};
